import {workingSet} from './mergeContext';
import store from '../store';
import Actions from '../actions/indexExport';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import newHRTimestamp from '../../lib/picos/timestamp';
import {findAll} from '../find';
import Campaign from '../../entities/campaign';
import Lead from '../../entities/lead';
import Task from '../../entities/task';
import Comment from '../../entities/comment';
import Document from '../../entities/document';
import {canCascadeDelete, addDeleteImpact, deleteImpactContext, deleteImpactCounts, workingRows, enqueueDelete} from './deleteContext';
import type {DeleteImpact, DeleteImpactContext, DeleteResult, DeleteTraversal} from './deleteContext';
import {deleteComment, collectCommentDeleteImpact} from './commentDeps';
import {deleteDocument, collectDocumentDeleteImpact} from './documentDeps';
export function canDeleteCampaign(): boolean {
return canCascadeDelete();
}
export function collectCampaignDeleteImpact(campaign: Campaign, context: DeleteImpactContext): void {
const key = 'campaign:' + campaign.id;
if (context.visited.has(key)) return;
context.visited.add(key);
const state = store.getState();
const leadCampaignsImpact = workingRows<Lead>('lead', findAll<Lead>('leads')).filter(leadRef => leadRef.campaigns?.includes(campaign.id));
leadCampaignsImpact.forEach(leadRef => addDeleteImpact(context, 'lead:' + leadRef.id, 'leads'));const taskTieInsImpact = workingRows<Task>('task', findAll<Task>('tasks')).filter(taskRef => taskRef.status !== 'D' && taskRef.hasOwnProperty('tieIns')).filter(taskRef => !!taskRef.tieIns?.some(f => {return 'Campaign' === f.entity && f.id === campaign.id;}));
taskTieInsImpact.forEach(task => addDeleteImpact(context, 'task:' + task.id, 'tasks'));workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'campaign' && comment.entityId === campaign.id).forEach(comment => {
if (context.visited.has('comment:' + comment.id)) return;
addDeleteImpact(context, 'comment:' + comment.id, 'comments');
collectCommentDeleteImpact(comment, context);
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'campaign' && document.entityId === campaign.id).forEach(document => {
if (context.visited.has('document:' + document.id)) return;
addDeleteImpact(context, 'document:' + document.id, 'documents');
collectDocumentDeleteImpact(document, context);
});
}
export function deleteImpactCampaign(campaign: Campaign): DeleteImpact {
const context = deleteImpactContext();
collectCampaignDeleteImpact(campaign, context);
return deleteImpactCounts(context);
}
export function deleteCampaign(campaign: Campaign, mergeId?: string, cascade?: DeleteTraversal): DeleteResult {
if (!cascade) {
if (!canDeleteCampaign()) return {status: 'notReady', impacts: []};
const context = deleteImpactContext();
collectCampaignDeleteImpact(campaign, context);
if (context.blocked) return {status: 'blocked', impacts: deleteImpactCounts(context)};
cascade = {visited: new Set(), importSource: mergeId ? 'merge' : 'delete', importId: mergeId || campaign.id};
}
const key = 'campaign:' + campaign.id;
if (cascade.visited.has(key)) return {status: 'deleted', impacts: []};
cascade.visited.add(key);
const targetImportSource = cascade.importSource;
mergeId = cascade.importId;
const _work = workingSet();
const dispatch = (action: any, key: string) => enqueueDelete(_work, onQueued => store.dispatch({...action, onQueued: (saved: boolean) => onQueued(saved, action.type.endsWith('_REMOVE_QUEUE'))}), () => _work.delete(key));
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'campaign' && comment.entityId === campaign.id).forEach(comment => {deleteComment(comment, mergeId, cascade);});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'campaign' && document.entityId === campaign.id).forEach(document => {deleteDocument(document, mergeId, cascade);});
workingRows<Lead>('lead', [...store.getState().leads.getMappingFor('campaignId', campaign.id)]).filter(leadTemp => leadTemp.campaigns?.includes(campaign.id)).forEach(leadTemp => {
const _lead = ((_work.get('lead:' + leadTemp.id) ?? leadTemp) as Lead).clone();
if (_lead.status === 'D') return;
_lead.status = 'U';
_lead.addedOn = newHRTimestamp();
_lead.addedBy = getCurrentUserId();
_lead.importSource = targetImportSource;
_lead.importId = mergeId;
_lead.campaigns = _lead.campaigns?.filter(id => id !== campaign.id) || [];

_work.set('lead:' + _lead.id, _lead);
dispatch(Actions.LeadActions.saveLeadQueue(_lead), 'lead:' + _lead.id);
});workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns')).filter(taskTemp => !!taskTemp.tieIns?.some(f => {return 'Campaign' === f.entity && f.id === campaign.id;})).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.tieIns = [...(_task.tieIns || [])].filter(f => !('Campaign' === f.entity && f.id === campaign.id));
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
dispatch(Actions.TaskActions.saveTaskQueue(_task), 'task:' + _task.id);
});
const deleted = campaign.clone();
deleted.status = 'D';
deleted.addedOn = newHRTimestamp();
deleted.addedBy = getCurrentUserId();
deleted.importSource = targetImportSource;
deleted.importId = mergeId;
_work.set(key, deleted);
const completion = dispatch(Actions.CampaignActions.removeCampaignQueue(deleted), key);
return {status: 'deleted', impacts: [], completion};
}

export function replaceCampaign(campaign: Campaign, replaceWith: Campaign, mergeId?: string) {
let targetImportSource = 'merge';
if (!mergeId) {targetImportSource = 'delete';mergeId = campaign.id;}
const _work = workingSet();
workingRows<Lead>('lead', [...store.getState().leads.getMappingFor('campaignId', campaign.id)]).filter(leadTemp => leadTemp.campaigns?.includes(campaign.id)).forEach(leadTemp => {
const _lead = ((_work.get('lead:' + leadTemp.id) ?? leadTemp) as Lead).clone();
if (_lead.status === 'D') return;
_lead.status = 'U';
_lead.addedOn = newHRTimestamp();
_lead.addedBy = getCurrentUserId();
_lead.importSource = targetImportSource;
_lead.importId = mergeId;
_lead.campaigns = _lead.campaigns?.map(id => (id === campaign.id) ? replaceWith.id : id) || [];

_work.set('lead:' + _lead.id, _lead);
store.dispatch(Actions.LeadActions.saveLeadQueue(_lead));
});


workingRows<Task>('task', findAll<Task>('tasks')).filter(taskTemp => taskTemp.status !== 'D' && taskTemp.hasOwnProperty('tieIns')).filter(taskTemp => !!taskTemp.tieIns?.some(f => {return 'Campaign' === f.entity && f.id === campaign.id;})).forEach(taskTemp => {
const _task = ((_work.get('task:' + taskTemp.id) ?? taskTemp) as Task).clone();
if (_task.status === 'D') return;
_task.tieIns = [...(_task.tieIns || [])].map(f => {if ('Campaign' === f.entity && f.id === campaign.id) {const _f = {...f}; _f.id = replaceWith.id; return _f;} else return f;});
_task.status = 'U';
_task.addedOn = newHRTimestamp();
_task.addedBy = getCurrentUserId();
_task.importSource = targetImportSource;
_task.importId = mergeId;
_work.set('task:' + _task.id, _task);
store.dispatch(Actions.TaskActions.saveTaskQueue(_task));
});
workingRows<Comment>('comment', findAll<Comment>('comments')).filter(comment => comment.status !== 'D' && comment.entityType === 'campaign' && comment.entityId === campaign.id).forEach(commentTemp => {
const _comment = ((_work.get('comment:' + commentTemp.id) ?? commentTemp) as Comment).clone();
if (_comment.status === 'D') return;
_comment.entityId = replaceWith.id;
_comment.status = 'U';
_comment.addedOn = newHRTimestamp();
_comment.addedBy = getCurrentUserId();
_comment.importSource = targetImportSource;
_comment.importId = mergeId;
_work.set('comment:' + _comment.id, _comment);
store.dispatch(Actions.CommentActions.saveCommentQueue(_comment));
});
workingRows<Document>('document', findAll<Document>('documents')).filter(document => document.status !== 'D' && document.entityType === 'campaign' && document.entityId === campaign.id).forEach(documentTemp => {
const _document = ((_work.get('document:' + documentTemp.id) ?? documentTemp) as Document).clone();
if (_document.status === 'D') return;
_document.entityId = replaceWith.id;
_document.status = 'U';
_document.addedOn = newHRTimestamp();
_document.addedBy = getCurrentUserId();
_document.importSource = targetImportSource;
_document.importId = mergeId;
_work.set('document:' + _document.id, _document);
store.dispatch(Actions.DocumentActions.saveDocumentQueue(_document));
});
return campaign;
}
